import { useState, useRef, useEffect, useMemo } from 'react';
import type { Exercise } from '../../types';

interface ExerciseAutocompleteProps {
  exercises: Exercise[];
  onSelect: (exercise: Exercise) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
}

export function ExerciseAutocomplete({
  exercises,
  onSelect,
  placeholder = 'Search exercises...',
  autoFocus = false,
  disabled = false,
  className = '',
}: ExerciseAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter exercises based on query
  const filteredExercises = useMemo(() => {
    if (!query.trim()) return exercises;
    const lowerQuery = query.toLowerCase();
    return exercises.filter((ex) =>
      ex.name.toLowerCase().includes(lowerQuery)
    );
  }, [exercises, query]);

  // Reset focused index when results change
  useEffect(() => {
    setFocusedIndex(0);
  }, [filteredExercises]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (exercise: Exercise) => {
    onSelect(exercise);
    // Keep dropdown open and query intact so user can quickly add similar exercises
    // User can press Escape or click outside to close
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredExercises.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredExercises[focusedIndex]) {
          handleSelect(filteredExercises[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setQuery('');
        break;
    }
  };

  const handleClear = () => {
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      {/* Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className="form-field w-full pr-10"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="exercise-autocomplete-list"
          aria-activedescendant={
            isOpen && filteredExercises[focusedIndex]
              ? `exercise-option-${focusedIndex}`
              : undefined
          }
        />

        {/* Search Icon / Clear Button */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {query && (
            <button
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-300 transition"
              type="button"
              aria-label="Clear search"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          {!query && (
            <svg
              className="w-4 h-4 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          id="exercise-autocomplete-list"
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/95 backdrop-blur-sm shadow-xl"
        >
          {filteredExercises.length === 0 ? (
            <div className="flex min-h-[44px] items-center justify-center px-4 py-2 text-center text-sm text-gray-600 dark:text-slate-400">
              {query ? 'No exercises found' : 'No exercises available'}
            </div>
          ) : (
            filteredExercises.map((exercise, index) => (
              <button
                key={exercise.id}
                id={`exercise-option-${index}`}
                role="option"
                aria-selected={index === focusedIndex}
                onClick={() => handleSelect(exercise)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={`
                  flex min-h-[44px] w-full flex-col justify-center px-4 py-2 text-left text-sm transition
                  ${
                    index === focusedIndex
                      ? 'bg-primary-500/20 text-gray-900 dark:text-white'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/5'
                  }
                  ${index > 0 ? 'border-t border-gray-200 dark:border-white/5' : ''}
                `}
              >
                <div className="font-medium leading-tight">{exercise.name}</div>
                {exercise.description && (
                  <div className="mt-0.5 line-clamp-1 text-xs leading-tight text-gray-600 dark:text-slate-400">
                    {exercise.description}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
